import CallCheck from './components/CallCheck';
// App.js — Main application shell.
//
// This file owns: state, socket wiring, routing, and top-level layout.
// All UI sub-components live in ./components/. Icons/themes in ./constants.
//
// ─── Component map ───────────────────────────────────────────────────────────
//  constants.js          — I (icons), THEMES, ThemeCtx, useTheme
//  debugLogger.js        — DebugLogger singleton
//  components/ui.jsx     — Avatar, Inp, Btn
//  components/VideoTile.jsx — VideoTile, SidebarVoiceMember
//  components/CallPanel.jsx — CallPanel, MobileCallView, ErrorBoundary
//  components/AdminPanel.jsx — AdminPanel
//  components/Login.jsx  — Login, MfaSetupModal
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { auth, tavern, getSocket, disconnectSocket } from './api';
import { I, THEMES, ThemeCtx } from './constants';
import DebugLogger from './debugLogger';
import { Avatar, Btn } from './components/ui';
import { SidebarVoiceMember } from './components/VideoTile';
import CallPanel, { MobileCallView, ErrorBoundary } from './components/CallPanel';
import AdminPanel from './components/AdminPanel';
import Login, { MfaSetupModal } from './components/Login';
import CirclesPanel from './components/CirclesPanel';
import TagsPanel from './components/TagsPanel';
import { TempRoomManager, TempRoomJoinPage } from './components/TempRoom';
import PushoverPanel from './components/PushoverPanel';
import LiveStream from './components/LiveStream';
import useWebRTC from './useWebRTC';
import { useElectron } from './useElectron';
import DownloadPage from './DownloadPage';

const isElectron = !!window.electronAPI;

export default function App() {
  const [user, setUser]                 = useState(null);
  const [authLoading, setAuthLoading]   = useState(!!localStorage.getItem('tavern_token'));
  const [socket, setSocket]             = useState(null);
  const [servers, setServers]           = useState([]);
  const [activeServer, setActiveServer] = useState(null);
  const [channels, setChannels]         = useState([]);
  const [members, setMembers]           = useState([]);
  const [onlineIds, setOnlineIds]       = useState(new Set());
  const [voiceRooms, setVoiceRooms]     = useState({});
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages]         = useState([]);
  const [input, setInput]               = useState('');
  const [typing, setTyping]             = useState([]);
  const [inCall, setInCall]             = useState(null);
  const [callChatOpen, setCallChatOpen] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [themeName, setThemeName]       = useState(() => {
    const saved = localStorage.getItem('tavern_theme');
    if (saved === 'nexus') { localStorage.setItem('tavern_theme', 'dark'); return 'dark'; }
    return saved || 'dark';
  });
  const C = THEMES[themeName] || THEMES.dark;
  const switchTheme = name => { localStorage.setItem('tavern_theme', name); setThemeName(name); };

  const [showMfaSetup, setShowMfaSetup]   = useState(false);
  const [mfaEnabled, setMfaEnabled]       = useState(false);
  const [toast, setToast]                 = useState(null);
  const [unread, setUnread]               = useState({});
  const [updateReady, setUpdateReady]     = useState(false);
  const [serverMenu, setServerMenu]       = useState(null);
  const [mobilePanel, setMobilePanel]     = useState('servers');
  const [windowWidth, setWindowWidth]     = useState(window.innerWidth);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [serverMembers, setServerMembers] = useState([]);
  const [showMemberPanel, setShowMemberPanel] = useState(false);
  const [showCircles, setShowCircles]     = useState(false);
  const [showTempRooms, setShowTempRooms] = useState(false);
  const [showPushover, setShowPushover]   = useState(false);
  const bottomRef   = useRef();
  const typingTimer = useRef();

  useEffect(() => {
    const h = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const isMobile = windowWidth <= 768;

  const rtc      = useWebRTC(socket);
  const electron = useElectron();

  useEffect(() => { electron.onUpdateDownloaded?.(() => setUpdateReady(true)); }, []); // eslint-disable-line

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const deleteChannel = ch => {
    if (!window.confirm(`Delete #${ch.name}? This cannot be undone.`)) return;
    tavern.deleteChannel(ch.id).then(() => {
      setChannels(p => p.filter(x => x.id !== ch.id));
      if (activeChannel?.id === ch.id) setActiveChannel(null);
    }).catch(e => showToast(e.message));
  };

  const isAdmin = user?.is_admin === 1;
  const openServerMenu = (e, s) => { e.preventDefault(); e.stopPropagation(); setServerMenu({ server: s, x: e.clientX, y: e.clientY }); };

  const deleteServer = s => {
    setServerMenu(null);
    if (!window.confirm(`Delete server "${s.name}"? This will delete ALL channels and messages. Cannot be undone.`)) return;
    tavern.deleteServer(s.id).then(() => {
      setServers(p => p.filter(x => x.id !== s.id));
      if (activeServer?.id === s.id) { setActiveServer(null); setChannels([]); setActiveChannel(null); }
      showToast('Server deleted');
    }).catch(e => showToast(e.message));
  };

  const renameServer = s => {
    setServerMenu(null);
    const name = prompt('New server name:', s.name);
    if (!name || name === s.name) return;
    tavern.updateServer(s.id, { name }).then(updated => {
      setServers(p => p.map(x => x.id === updated.id ? updated : x));
      if (activeServer?.id === updated.id) setActiveServer(updated);
    }).catch(e => showToast(e.message));
  };

  const openMemberPanel = () => {
    if (!activeServer) return;
    tavern.getMembers(activeServer.id).then(m => { setServerMembers(m); setShowMemberPanel(true); });
  };

  const kickMember = uid => {
    if (!window.confirm('Kick this member?')) return;
    tavern.kickMember(activeServer.id, uid).then(() => setServerMembers(p => p.filter(x => x.id !== uid)));
  };

  const banMember = (uid, username) => {
    if (!window.confirm(`Ban ${username}? They won't be able to rejoin.`)) return;
    tavern.banMember(activeServer.id, uid).then(() => setServerMembers(p => p.filter(x => x.id !== uid)));
  };

  const openAdminPanel = () => { setShowAdminPanel(true); setServerMenu(null); };

  // ── Init from token ────────────────────────────────────────────────────────
  useEffect(() => {
    // If visiting /join/:code — render guest join page (no auth needed)
    // We detect this here; actual rendering is in the return block
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) { localStorage.setItem('tavern_token', urlToken); window.history.replaceState({}, '', '/'); }
    const token = localStorage.getItem('tavern_token');
    if (token) {
      DebugLogger.setToken(token);
      auth.me()
        .then(u => { setUser(u); setMfaEnabled(u.mfa_enabled); })
        .catch(() => localStorage.removeItem('tavern_token'))
        .finally(() => setAuthLoading(false));
    }
  }, []);

  // ── Socket ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('tavern_token');
    const s = getSocket(token);
    setSocket(s);

    s.on('connect', () => s.emit('join_servers'));
    s.on('online_list',  ({ userIds }) => setOnlineIds(new Set([String(user.id), ...userIds.map(String)])));
    s.on('user_online',  ({ userId }) => setOnlineIds(p => new Set([...p, String(userId)])));
    s.on('user_offline', ({ userId }) => setOnlineIds(p => { const n = new Set(p); n.delete(String(userId)); return n; }));
    s.on('new_message', msg => {
      setMessages(m => [...m, msg]);
      setUnread(u => {
        const next = { ...u, [msg.channel_id]: (u[msg.channel_id] || 0) + 1 };
        const total = Object.values(next).reduce((a, b) => a + b, 0);
        electron.setBadge(total);
        if (msg.user_id !== user.id) electron.notify(msg.username, msg.content);
        return next;
      });
    });
    s.on('message_updated', ({ messageId, content, edited_at }) =>
      setMessages(m => m.map(msg => msg.id === messageId ? { ...msg, content, edited_at } : msg)));
    s.on('message_deleted', ({ messageId }) =>
      setMessages(m => m.filter(msg => msg.id !== messageId)));
    s.on('reactions_updated', ({ messageId, reactions }) =>
      setMessages(m => m.map(msg => msg.id === messageId ? { ...msg, reactions } : msg)));
    s.on('typing_start', ({ userId: uid, username }) => {
      if (uid === user.id) return;
      setTyping(t => [...t.filter(x => x.userId !== uid), { userId: uid, username }]);
    });
    s.on('typing_stop', ({ userId: uid }) => setTyping(t => t.filter(x => x.userId !== uid)));
    s.on('voice_room_update', ({ channelId, members: roomMembers }) => {
      setVoiceRooms(v => ({ ...v, [channelId]: roomMembers }));
      setMembers(current => {
        const currentIds = new Set(current.map(m => String(m.id)));
        const hasNew = roomMembers.some(m => !currentIds.has(String(m.userId)));
        if (hasNew && activeServer) tavern.getMembers(activeServer.id).then(fresh => setMembers(fresh));
        return current;
      });
    });
    s.on('private_call_invite', ({ roomId, fromUsername, withVideo }) => setIncomingCall({ roomId, fromUsername, withVideo }));

    return () => { s.off(); };
  }, [user]); // eslint-disable-line

  // ── Load servers ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    tavern.getVisibleServers().then(sv => { setServers(sv); if (sv.length) setActiveServer(sv[0]); });
  }, [user]);

  // ── Load channels + members ────────────────────────────────────────────────
  useEffect(() => {
    if (!activeServer) return;
    Promise.all([tavern.getChannels(activeServer.id), tavern.getMembers(activeServer.id)]).then(([chs, mbs]) => {
      setChannels(chs); setMembers(mbs);
      const first = chs.find(c => c.type === 'text');
      if (first) setActiveChannel(first);
    });
  }, [activeServer]);

  // ── Load messages ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeChannel || activeChannel.type !== 'text') return;
    tavern.getMessages(activeChannel.id).then(msgs => { setMessages(msgs); setUnread(u => ({ ...u, [activeChannel.id]: 0 })); });
    socket?.emit('join_channel', { channelId: activeChannel.id });
  }, [activeChannel, socket]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleInput = val => {
    setInput(val);
    if (!activeChannel || !socket) return;
    socket.emit('typing_start', { channelId: activeChannel.id });
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => socket.emit('typing_stop', { channelId: activeChannel.id }), 2000);
  };

  const sendMessage = () => {
    if (!input.trim() || !activeChannel || !socket) return;
    socket.emit('send_message', { channelId: activeChannel.id, content: input.trim() });
    socket.emit('typing_stop', { channelId: activeChannel.id });
    setInput('');
  };

  const toggleReaction = (msgId, emoji) => { socket?.emit('toggle_reaction', { messageId: msgId, emoji }); };

  const joinVoice = useCallback(async (ch, withVideo = false) => {
    if (inCall) { rtc.leaveChannel(); setInCall(null); }
    await rtc.joinChannel(ch.id, { video: withVideo });
    setInCall({ channelId: ch.id, name: ch.name });
    setMobilePanel('chat');
  }, [inCall, rtc]);

  const startPrivateCall = useCallback(async (targetUser, withVideo = false) => {
    if (inCall) { rtc.leaveChannel(); setInCall(null); }
    const roomId   = `private:${[user.id, targetUser.id].sort().join(':')}`;
    const roomName = `Call with ${targetUser.username}`;
    await rtc.joinChannel(roomId, { video: withVideo });
    setInCall({ channelId: roomId, name: roomName, isPrivate: true, targetUser });
    if (socket) socket.emit('private_call_invite', { toUserId: targetUser.id, roomId, fromUsername: user.username, withVideo });
    if (window.innerWidth <= 768) setMobilePanel('chat');
  }, [inCall, rtc, socket, user]);

  const leaveCall = useCallback(() => { rtc.leaveChannel(); setInCall(null); }, [rtc]);
  const logout    = () => { localStorage.removeItem('tavern_token'); disconnectSocket(); setUser(null); setSocket(null); };

  // ── Routing ────────────────────────────────────────────────────────────────
  if (window.location.pathname === '/download') return <DownloadPage/>;
  // /live is public but we wait for auth to resolve so logged-in users get their username
  // authLoading prevents the name gate flashing before user is known
  if (window.location.pathname === '/live') {
    if (authLoading) return null; // wait for token check
    return <LiveStream user={user}/>;
  }
  if (!user) return (
    <ThemeCtx.Provider value={C}>
      <Login onLogin={u => { setUser(u); setMfaEnabled(u.mfa_enabled); }}/>
    </ThemeCtx.Provider>
  );

  // ── Theme-specific CSS ─────────────────────────────────────────────────────
  const themeCSS = themeName === 'tavern' ? `
    .tv-heading { font-family: 'Cinzel', Georgia, serif !important; letter-spacing: 0.05em; }
    .tv-flicker { animation: candleFlicker 3s ease-in-out infinite; }
    .tv-channel-btn:hover { background: rgba(200,146,42,0.12) !important; border-color: rgba(200,146,42,0.3) !important; }
    .tv-channel-btn.active { background: rgba(200,146,42,0.18) !important; color: #C8922A !important; }
    .tv-input { border: 1px solid #3D2A14 !important; background: #140E06 !important; }
    .tv-input:focus { border-color: #C8922A88 !important; box-shadow: 0 0 0 3px #C8922A18 !important; }
    .tv-badge { background: linear-gradient(135deg, #C8922A, #8B5E1A) !important; color: #1A1008 !important; }
  ` : `
    .tv-channel-btn:hover { background: rgba(0,212,170,0.08) !important; }
    .tv-channel-btn.active { background: rgba(0,212,170,0.12) !important; color: #00D4AA !important; }
    .tv-msg:hover { background: rgba(255,255,255,0.02) !important; }
  `;

  const WinTitlebar = isElectron && electron.platform === 'win32' ? (
    <div style={{ height: 32, background: C.rail, display: 'flex', alignItems: 'center', justifyContent: 'space-between', WebkitAppRegion: 'drag', flexShrink: 0, borderBottom: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 12, WebkitAppRegion: 'no-drag' }}>
        <div style={{ width: 18, height: 18, borderRadius: 5, background: `linear-gradient(135deg,#00B896,${C.accent})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: '#0A0C10' }}>T</div>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>Tavern</span>
      </div>
      <div style={{ display: 'flex', WebkitAppRegion: 'no-drag' }}>
        {[{ label: '─', action: electron.minimize }, { label: '□', action: electron.maximize }, { label: '✕', action: electron.close, danger: true }].map(b => (
          <button key={b.label} onClick={b.action} style={{ width: 46, height: 32, border: 'none', background: 'transparent', color: b.danger ? '#EF4444' : C.muted, fontSize: b.label === '✕' ? 14 : 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseEnter={e => e.currentTarget.style.background = b.danger ? '#EF444433' : '#1A1D24'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>{b.label}</button>
        ))}
      </div>
    </div>
  ) : null;

  const textChannels   = channels.filter(c => c.type === 'text');
  const voiceChannels  = channels.filter(c => c.type === 'voice');
  const voiceOnlineIds = new Set(Object.values(voiceRooms).flatMap(ms => ms.map(m => String(m.userId))));
  const effectiveOnlineIds = new Set([...onlineIds, ...voiceOnlineIds]);
  const onlineMembers  = members.filter(m => effectiveOnlineIds.has(String(m.id)));
  const offlineMembers = members.filter(m => !effectiveOnlineIds.has(String(m.id)));

  // ── /join/:code — guest temp room entry point ─────────────────────────────
  const joinMatch = window.location.pathname.match(/^\/join\/([a-f0-9]{8})$/i);
  if (joinMatch) {
    return (
      <ThemeCtx.Provider value={C}>
        <TempRoomJoinPage code={joinMatch[1].toLowerCase()} />
      </ThemeCtx.Provider>
    );
  }

  return (
    <ThemeCtx.Provider value={C}>
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: C.bg, fontFamily: C.font, color: C.text, overflow: 'hidden' }}>
      {WinTitlebar}
      {updateReady && (
        <div style={{ background: '#F59E0B', color: '#0A0C10', padding: '8px 20px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexShrink: 0 }}>
          ⬆ A new version of Tavern is ready.
          <button onClick={() => electron.installUpdate()} style={{ background: '#0A0C10', color: '#F59E0B', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>Restart &amp; Update</button>
        </div>
      )}

      {/* ── Modals ── */}
      {showCircles   && isAdmin && <TagsPanel onClose={() => setShowCircles(false)} />}
      {showTempRooms && <TempRoomManager user={user} onClose={() => setShowTempRooms(false)} />}
      {showPushover  && isAdmin && <PushoverPanel onClose={() => setShowPushover(false)} />}

      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono&family=Crimson+Pro:ital,wght@0,400;0,600;0,700;1,400&family=Cinzel:wght@700;900&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{width:5px;}
        ::-webkit-scrollbar-thumb{background:${C.scrollbar};border-radius:4px;}
        ::-webkit-scrollbar-track{background:transparent;}
        input::placeholder{color:${C.muted}88;}
        input,textarea,select{font-family:${C.font};}
        @keyframes fadeUp{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:translateY(0);}}
        @keyframes candleFlicker{0%,100%{opacity:1;text-shadow:0 0 8px #C8922A88;}50%{opacity:0.88;text-shadow:0 0 14px #C8922Acc,0 0 28px #C8922A44;}}
        @keyframes emberPulse{0%,100%{box-shadow:0 0 8px ${C.accent}44;}50%{box-shadow:0 0 20px ${C.accent}88,0 0 40px ${C.accent}22;}}
        ${themeCSS}
        @media(max-width:768px){.mob-hide{display:none!important;}.mob-full{width:100%!important;max-width:100vw!important;}}
      `}</style>

      {toast && <div className="tv-badge" style={{ position: 'fixed', top: 20, right: 20, background: C.accent, color: '#0A0C10', padding: '11px 20px', borderRadius: 12, fontWeight: 700, zIndex: 500, fontSize: 14, animation: 'fadeUp 0.3s ease' }}>{toast}</div>}

      {showMfaSetup && <MfaSetupModal onClose={() => setShowMfaSetup(false)} onDone={() => { setShowMfaSetup(false); setMfaEnabled(true); showToast('✓ MFA enabled!'); }}/>}

      {/* Settings modal */}
      {showSettings && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, width: 460, maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: C.text }}>Settings</div>
              <button onClick={() => setShowSettings(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted }}><I.X/></button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <CallCheck socket={socket}/>
              <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Profile</div>
              <div style={{ background: '#0D0F14', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
                <Avatar u={user} size={44}/>
                <div>
                  <div style={{ fontWeight: 700, color: C.text }}>{user.username}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{user.email || user.phone || 'No email'}</div>
                </div>
              </div>
              <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Security</div>
              <div style={{ background: '#0D0F14', borderRadius: 12, overflow: 'hidden', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                      Two-Factor Authentication
                      {mfaEnabled && <span style={{ background: `${C.accent}22`, color: C.accent, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>Active</span>}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{mfaEnabled ? 'Your account has extra protection' : 'Add a second layer of security'}</div>
                  </div>
                  {!mfaEnabled && <button onClick={() => { setShowSettings(false); setShowMfaSetup(true); }} style={{ background: '#1A1D24', border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 12, fontWeight: 600, padding: '6px 14px', cursor: 'pointer' }}>Enable</button>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Login Provider</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2, textTransform: 'capitalize' }}>{user.provider || 'local'}</div>
                  </div>
                </div>
              </div>
              <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Appearance</div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
                {Object.entries(THEMES).map(([key, t]) => (
                  <button key={key} onClick={() => switchTheme(key)} style={{ flex: 1, padding: '14px 10px', borderRadius: 12, cursor: 'pointer', border: themeName === key ? `2px solid ${C.accent}` : `2px solid ${C.border}`, background: t.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, boxShadow: themeName === key ? `0 0 16px ${C.accent}44` : 'none', transition: 'all 0.2s' }}>
                    <div style={{ fontSize: 24 }}>{t.emoji}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text, fontFamily: t.font }}>{t.name}</div>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {[t.bg, t.sidebar, t.accent, t.text].map((col, i) => <div key={i} style={{ width: 12, height: 12, borderRadius: '50%', background: col, border: `1px solid ${t.border}` }}/>)}
                    </div>
                    {themeName === key && <div style={{ fontSize: 10, color: t.accent, fontWeight: 700 }}>Active</div>}
                  </button>
                ))}
              </div>
              <a href="/download" style={{ display: 'block', textAlign: 'center', padding: '11px 0', borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.accent, fontSize: 14, fontWeight: 700, textDecoration: 'none', marginBottom: 8 }}>⬇ Download Windows App</a>

              {/* ── New feature buttons ── */}
              <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, marginTop: 8 }}>Features</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                {isAdmin && (
                  <button onClick={() => { setShowSettings(false); setShowCircles(true); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.text, cursor: 'pointer', textAlign: 'left' }}>
                    <span style={{ fontSize: 18 }}>🏷️</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>Tags</div>
                      <div style={{ fontSize: 11, color: C.muted }}>Control who can see who and which servers they access</div>
                    </div>
                    <span style={{ color: C.muted, fontSize: 16 }}>›</span>
                  </button>
                )}
                <button onClick={() => { setShowSettings(false); setShowTempRooms(true); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.text, cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ fontSize: 18 }}>🚪</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>Temp Rooms</div>
                    <div style={{ fontSize: 11, color: C.muted }}>Share a link — guests join by name, no account needed</div>
                  </div>
                  <span style={{ color: C.muted, fontSize: 16 }}>›</span>
                </button>
                {isAdmin && (
                  <button onClick={() => { setShowSettings(false); setShowPushover(true); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.text, cursor: 'pointer', textAlign: 'left' }}>
                    <span style={{ fontSize: 18 }}>🔔</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>Pushover Notifications</div>
                      <div style={{ fontSize: 11, color: C.muted }}>Get notified of logins, registrations &amp; activity</div>
                    </div>
                    <span style={{ color: C.muted, fontSize: 16 }}>›</span>
                  </button>
                )}
              </div>

              <Btn variant="danger" onClick={logout}>Log Out</Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── 1. Server Rail ── */}
      {(!isMobile || mobilePanel === 'servers') && !inCall && (
        isMobile ? (
          <div style={{ width: '100%', height: '100%', background: C.sidebar, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '20px 20px 12px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.text, fontFamily: C.font }}>{themeName === 'tavern' ? '🍺 The Tavern' : '⚡ Tavern'}</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Choose a server</div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '12px 12px' }}>
              {servers.map(s => {
                const isActive = activeServer?.id === s.id;
                return (
                  <button key={s.id} onClick={() => { setActiveServer(s); setMobilePanel('channels'); }} onContextMenu={e => openServerMenu(e, s)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 14, border: isActive ? `1px solid ${C.accent}55` : `1px solid ${C.border}`, cursor: 'pointer', marginBottom: 6, background: isActive ? `${C.accent}18` : C.card, textAlign: 'left', transition: 'all 0.15s' }}>
                    <div style={{ width: 46, height: 46, borderRadius: 14, flexShrink: 0, background: isActive ? s.color || C.accent : C.rail, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{s.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: isActive ? C.accent : C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                    </div>
                    {isActive && <div style={{ color: C.accent, fontSize: 18 }}>›</div>}
                  </button>
                );
              })}
              <button onClick={() => { const name = prompt('Server name?'); if (name) tavern.createServer({ name }).then(s => { setServers(p => [...p, s]); setActiveServer(s); setMobilePanel('channels'); }); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 14, border: `1px dashed ${C.border}`, cursor: 'pointer', background: 'transparent', marginBottom: 6 }}>
                <div style={{ width: 46, height: 46, borderRadius: 14, background: C.rail, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.accent, fontSize: 20 }}>+</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.muted }}>Create a Server</div>
              </button>
            </div>
            <div style={{ padding: '12px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <Avatar u={user} size={34}/>
              <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 14, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.username}</div></div>
              <button onClick={() => window.open('/live?stream=1', '_blank')} title="Go Live"
                style={{ background: '#EF444422', border: '1px solid #EF444444', borderRadius: 8, color: '#EF4444', padding: '6px 10px', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>🔴 Live</button>
              <button onClick={() => window.open('/live', '_blank')} title="Watch Stream"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 18, padding: 4 }}>📺</button>
              {isAdmin && <button onClick={openAdminPanel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.accent, fontSize: 18, padding: 4 }}>🛡</button>}
              <button onClick={() => setShowSettings(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex', padding: 4 }}><I.Gear/></button>
            </div>
          </div>
        ) : (
          <div style={{ width: 64, background: C.rail, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 0', gap: 8, borderRight: `1px solid ${C.border}`, flexShrink: 0, overflowY: 'auto' }}>
            {servers.map(s => (
              <button key={s.id} onClick={() => setActiveServer(s)} onContextMenu={e => openServerMenu(e, s)} title={s.name}
                style={{ width: 44, height: 44, borderRadius: activeServer?.id === s.id ? 14 : '50%', border: 'none', cursor: 'pointer', background: activeServer?.id === s.id ? s.color || C.accent : C.card, fontSize: 20, transition: 'all 0.2s', flexShrink: 0, boxShadow: activeServer?.id === s.id ? `0 0 16px ${s.color || C.accent}55` : 'none' }}>{s.icon}</button>
            ))}
            <div style={{ width: 30, height: 1, background: C.border, margin: '4px 0' }}/>
            <button title="Create Server" onClick={() => { const name = prompt('Server name?'); if (name) tavern.createServer({ name }).then(s => { setServers(p => [...p, s]); setActiveServer(s); }); }}
              style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer', background: C.card, color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><I.Plus/></button>
            <div style={{ width: 30, height: 1, background: C.border, margin: '4px 0' }}/>
            <button title="Go Live" onClick={() => window.open('/live?stream=1', '_blank')}
              style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer', background: '#EF444422', color: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>🔴</button>
            <button title="Watch Stream" onClick={() => window.open('/live', '_blank')}
              style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer', background: C.card, color: C.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>📺</button>
          </div>
        )
      )}

      {/* ── 2. Channel Sidebar ── */}
      {(!isMobile || mobilePanel === 'channels') && !inCall && (
        <div style={{ width: isMobile ? '100%' : 220, background: '#0E1015', display: 'flex', flexDirection: 'column', borderRight: isMobile ? 'none' : `1px solid ${C.border}` }}>
          <div style={{ padding: '14px 14px 10px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {isMobile && <button onClick={() => setMobilePanel('servers')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 20, padding: '0 8px 0 0', lineHeight: 1 }}>‹</button>}
            <div className="tv-heading" style={{ fontWeight: 700, fontSize: 14, color: C.text, flex: 1 }}>{activeServer?.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={openMemberPanel} title="Manage members" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 14, padding: 2, display: 'flex' }}>👥</button>
              <button onClick={e => openServerMenu(e, activeServer)} title="Server settings" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 14, padding: 2, display: 'flex' }}><I.Gear/></button>
            </div>
          </div>

          <div style={{ flex: 1, overflow: 'auto', padding: '10px 8px' }}>
            <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, padding: '6px 8px 4px' }}>Text Channels</div>
            {textChannels.map(c => (
              <div key={c.id} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <button className={`tv-channel-btn${activeChannel?.id === c.id ? ' active' : ''}`}
                  onClick={() => { setActiveChannel(c); setUnread(u => ({ ...u, [c.id]: 0 })); if (isMobile) setMobilePanel('chat'); if (inCall && !isMobile) setCallChatOpen(true); }}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', background: activeChannel?.id === c.id ? '#1A1D2A' : 'transparent', color: activeChannel?.id === c.id ? C.text : C.muted, textAlign: 'left' }}>
                  <I.Hash/> <span style={{ flex: 1, fontSize: 14, fontWeight: activeChannel?.id === c.id ? 600 : 400 }}>{c.name}</span>
                  {unread[c.id] > 0 && activeChannel?.id !== c.id && <span style={{ background: '#EF4444', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '1px 6px' }}>{unread[c.id]}</span>}
                </button>
                <button onClick={() => deleteChannel(c)} title="Delete channel" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 14, padding: '4px 6px', opacity: 0.4, borderRadius: 6, lineHeight: 1 }}
                  onMouseEnter={e => e.currentTarget.style.opacity = 1}
                  onMouseLeave={e => e.currentTarget.style.opacity = 0.4}>✕</button>
              </div>
            ))}

            <div className="tv-heading" style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, padding: '14px 8px 4px' }}>Voice Channels</div>
            {voiceChannels.map(c => {
              const roomMembers = voiceRooms[c.id] || [];
              return (
                <div key={c.id}>
                  <button className={`tv-channel-btn${inCall?.channelId === c.id ? ' active' : ''}`} onClick={() => joinVoice(c)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'transparent', color: inCall?.channelId === c.id ? C.accent : C.muted, textAlign: 'left' }}>
                    <I.Vol/> <span style={{ fontSize: 14, flex: 1 }}>{c.name}</span>
                    {roomMembers.length > 0 && <span style={{ fontSize: 11, color: C.muted }}>{roomMembers.length}</span>}
                  </button>
                  {roomMembers.map(m => {
                    const peerEntry = Object.values(rtc.peers).find(p => p.userId === m.userId);
                    const isMe = m.userId === user?.id;
                    return <SidebarVoiceMember key={m.socketId} member={m} stream={isMe ? rtc.localStream : peerEntry?.stream} isMe={isMe}/>;
                  })}
                </div>
              );
            })}
          </div>

          <div style={{ padding: '10px', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative' }}>
              <Avatar u={user} size={30}/>
              <div style={{ position: 'absolute', bottom: -1, right: -1, width: 9, height: 9, borderRadius: '50%', background: '#22C55E', border: `2px solid ${C.sidebar}` }}/>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.username}</div>
            </div>
            {isAdmin && <button onClick={openAdminPanel} title="Admin Panel" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.accent, fontSize: 16, padding: 4 }}>🛡</button>}
            <button onClick={() => setShowSettings(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex', padding: 4 }}><I.Gear/></button>
          </div>
        </div>
      )}

      {/* ── 3. Chat / Call column ── */}
      {/* On mobile: when in a call, the call view is ALWAYS rendered (never unmounted) so WebRTC stays alive.
          Server/channel panels slide in as overlays on top. */}
      {(!isMobile || mobilePanel === 'chat' || inCall) && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.chat, minWidth: 0, minHeight: 0, overflow: 'hidden', position: 'relative' }}>

          {/* ── Mobile in-call: always rendered, never unmounted ── */}
          {inCall && isMobile && (
            <>
              {/* Full-screen call view — always in DOM while inCall */}
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', zIndex: 1 }}>
                <MobileCallView channelName={inCall.name} rtc={rtc} onLeave={leaveCall} user={user} isAdmin={isAdmin}
                  onOpenChannels={() => setMobilePanel('channels')}
                  chatSlot={
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                      <div style={{ padding: '6px 10px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 4, flexWrap: 'wrap', flexShrink: 0 }}>
                        {channels.filter(ch => ch.type === 'text').map(ch => (
                          <button key={ch.id} onClick={() => { setActiveChannel(ch); setUnread(u => ({ ...u, [ch.id]: 0 })); }}
                            style={{ padding: '4px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: activeChannel?.id === ch.id ? 700 : 400, background: activeChannel?.id === ch.id ? `${C.accent}33` : '#1A1D24', color: activeChannel?.id === ch.id ? C.accent : C.muted }}>
                            #{ch.name}{unread[ch.id] > 0 ? ` (${unread[ch.id]})` : ''}
                          </button>
                        ))}
                      </div>
                      <div style={{ flex: 1, overflow: 'auto', padding: '8px 12px' }}>
                        {messages.filter(m => String(m.channel_id) === String(activeChannel?.id)).map(msg => (
                          <div key={msg.id} style={{ padding: '4px 0', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                            <Avatar u={{ username: msg.username, color: msg.color, avatar: msg.avatar }} size={26}/>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{msg.username} <span style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>{msg.created_at ? new Date(msg.created_at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span></div>
                              <div style={{ fontSize: 13, color: C.text, lineHeight: 1.4, wordBreak: 'break-word' }}>{msg.content}</div>
                            </div>
                          </div>
                        ))}
                        <div ref={bottomRef}/>
                      </div>
                      <div style={{ padding: '8px 10px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                        <input value={input} onChange={e => setInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                          placeholder={activeChannel ? `#${activeChannel.name}` : 'Select a channel'}
                          style={{ flex: 1, background: '#111318', border: `1px solid ${C.border}`, borderRadius: 20, color: C.text, fontSize: 13, outline: 'none', padding: '8px 14px' }}/>
                        <button onClick={sendMessage} style={{ background: input.trim() ? C.accent : '#1A1D24', border: 'none', borderRadius: '50%', cursor: 'pointer', color: input.trim() ? '#0A0C10' : C.muted, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}><I.Send/></button>
                      </div>
                    </div>
                  }
                />
              </div>

              {/* Channel panel overlay — slides in from left when navigating away from call view */}
              {mobilePanel === 'channels' && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 10, background: '#0E1015', display: 'flex', flexDirection: 'column', animation: 'slideIn 0.22s ease' }}>
                  <div style={{ padding: '14px 14px 10px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <button onClick={() => setMobilePanel('chat')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 20, padding: '0 8px 0 0', lineHeight: 1 }}>‹</button>
                    <div className="tv-heading" style={{ fontWeight: 700, fontSize: 14, color: C.text, flex: 1 }}>{activeServer?.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {/* In-call indicator */}
                      <div style={{ background: '#22C55E22', border: '1px solid #22C55E55', borderRadius: 20, padding: '3px 10px', fontSize: 11, color: '#22C55E', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22C55E', animation: 'emberPulse 1.5s ease-in-out infinite' }}/>
                        In call
                      </div>
                    </div>
                  </div>
                  <div style={{ flex: 1, overflow: 'auto', padding: '10px 8px' }}>
                    <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, padding: '6px 8px 4px' }}>Text Channels</div>
                    {textChannels.map(c => (
                      <button key={c.id} className={`tv-channel-btn${activeChannel?.id === c.id ? ' active' : ''}`}
                        onClick={() => { setActiveChannel(c); setUnread(u => ({ ...u, [c.id]: 0 })); setMobilePanel('chat'); }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', background: activeChannel?.id === c.id ? '#1A1D2A' : 'transparent', color: activeChannel?.id === c.id ? C.text : C.muted, textAlign: 'left', marginBottom: 2 }}>
                        <I.Hash/> <span style={{ flex: 1, fontSize: 14 }}>{c.name}</span>
                        {unread[c.id] > 0 && <span style={{ background: '#EF4444', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '1px 6px' }}>{unread[c.id]}</span>}
                      </button>
                    ))}
                    <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, padding: '14px 8px 4px' }}>Voice Channels</div>
                    {voiceChannels.map(c => (
                      <button key={c.id} className={`tv-channel-btn${inCall?.channelId === c.id ? ' active' : ''}`}
                        onClick={() => { joinVoice(c); setMobilePanel('chat'); }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'transparent', color: inCall?.channelId === c.id ? C.accent : C.muted, textAlign: 'left', marginBottom: 2 }}>
                        <I.Vol/> <span style={{ fontSize: 14, flex: 1 }}>{c.name}</span>
                        {(voiceRooms[c.id]?.length > 0) && <span style={{ fontSize: 11, color: C.muted }}>{voiceRooms[c.id].length}</span>}
                      </button>
                    ))}
                  </div>
                  <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <Avatar u={user} size={30}/>
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.text }}>{user.username}</div>
                    {isAdmin && <button onClick={openAdminPanel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.accent, fontSize: 16, padding: 4 }}>🛡</button>}
                    <button onClick={() => setShowSettings(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex', padding: 4 }}><I.Gear/></button>
                  </div>
                </div>
              )}

              {/* Server list overlay */}
              {mobilePanel === 'servers' && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 11, background: C.sidebar, display: 'flex', flexDirection: 'column', animation: 'slideIn 0.22s ease' }}>
                  <div style={{ padding: '20px 20px 12px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button onClick={() => setMobilePanel('channels')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 20, lineHeight: 1 }}>‹</button>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: C.text, fontFamily: C.font }}>{themeName === 'tavern' ? '🍺 The Tavern' : '⚡ Tavern'}</div>
                    </div>
                  </div>
                  <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
                    {servers.map(s => {
                      const isActive = activeServer?.id === s.id;
                      return (
                        <button key={s.id} onClick={() => { setActiveServer(s); setMobilePanel('channels'); }} onContextMenu={e => openServerMenu(e, s)}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 14, border: isActive ? `1px solid ${C.accent}55` : `1px solid ${C.border}`, cursor: 'pointer', marginBottom: 6, background: isActive ? `${C.accent}18` : C.card, textAlign: 'left' }}>
                          <div style={{ width: 46, height: 46, borderRadius: 14, flexShrink: 0, background: isActive ? s.color || C.accent : C.rail, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{s.icon}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 15, fontWeight: 700, color: isActive ? C.accent : C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                          </div>
                          {isActive && <div style={{ color: C.accent, fontSize: 18 }}>›</div>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {inCall && !isMobile && (
            <ErrorBoundary>
              <CallPanel channelName={inCall.name} rtc={rtc} onLeave={leaveCall} chatOpen={callChatOpen} setChatOpen={setCallChatOpen} user={user}
                chatSlot={activeChannel ? (
                  <>
                    <div style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 4, flexWrap: 'wrap', flexShrink: 0 }}>
                      {channels.filter(ch => ch.type === 'text').map(ch => (
                        <button key={ch.id} onClick={() => { setActiveChannel(ch); setUnread(u => ({ ...u, [ch.id]: 0 })); }}
                          style={{ padding: '3px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: activeChannel?.id === ch.id ? 700 : 400, background: activeChannel?.id === ch.id ? `${C.accent}22` : 'transparent', color: activeChannel?.id === ch.id ? C.accent : C.muted }}>
                          # {ch.name}{unread[ch.id] > 0 ? ` (${unread[ch.id]})` : ''}
                        </button>
                      ))}
                    </div>
                    <div style={{ flex: 1, overflow: 'auto', padding: '8px 10px' }}>
                      {messages.filter(m => String(m.channel_id) === String(activeChannel?.id)).map(msg => (
                        <div key={msg.id} style={{ padding: '4px 0', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                          <Avatar u={{ username: msg.username, color: msg.color, avatar: msg.avatar }} size={24}/>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{msg.username} </span>
                            <span style={{ fontSize: 12, color: C.muted, wordBreak: 'break-word' }}>{msg.content}</span>
                          </div>
                        </div>
                      ))}
                      <div ref={el => { if (el) el.scrollIntoView({ behavior: 'smooth' }); }}/>
                    </div>
                    <div style={{ padding: '8px 10px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                      <input value={input} onChange={e => setInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                        placeholder={`Message #${activeChannel?.name}`}
                        style={{ flex: 1, background: C.inputBg || C.card, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, outline: 'none', padding: '8px 10px' }}/>
                      <button onClick={sendMessage} style={{ background: input.trim() ? C.accent : C.card, border: 'none', borderRadius: 8, cursor: 'pointer', color: input.trim() ? '#0A0C10' : C.muted, padding: '7px 10px', display: 'flex' }}><I.Send/></button>
                    </div>
                  </>
                ) : <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: 13 }}>No channel selected</div>}/>
            </ErrorBoundary>
          )}

          {(!inCall || isMobile) && (
            <>
              {!activeChannel && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: C.muted, gap: 16, padding: 40, textAlign: 'center' }}>
                  {themeName === 'tavern' ? (<>
                    <div style={{ fontSize: 64, filter: 'drop-shadow(0 0 24px #C8922A88)', animation: 'candleFlicker 3s ease-in-out infinite' }}>🍺</div>
                    <div className="tv-heading tv-flicker" style={{ fontSize: 24, fontWeight: 700, color: C.accent, letterSpacing: 2 }}>Welcome to the Tavern</div>
                    <div style={{ fontSize: 15, color: C.muted, fontStyle: 'italic', maxWidth: 300, lineHeight: 1.6 }}>"An adventurer walks in, takes a seat by the fire, and listens to the murmur of the crowd..."</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Select a channel from the board to begin</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, opacity: 0.4 }}>
                      <div style={{ width: 60, height: 1, background: `linear-gradient(to right, transparent, ${C.border})` }}/><span style={{ fontSize: 14 }}>🕯️</span><div style={{ width: 60, height: 1, background: `linear-gradient(to left, transparent, ${C.border})` }}/>
                    </div>
                  </>) : (<>
                    <div style={{ fontSize: 48 }}>⚡</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>Pick a channel</div>
                    <div style={{ fontSize: 13 }}>Select a text channel from the sidebar to start chatting</div>
                  </>)}
                </div>
              )}
              {activeChannel && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <div style={{ padding: '0 20px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 15, color: C.text }}>
                      <button onClick={() => { setActiveChannel(null); if (isMobile) setMobilePanel('channels'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 18, padding: '0 4px 0 0', lineHeight: 1, display: 'flex', alignItems: 'center' }}>‹</button>
                      {activeChannel?.type === 'text' ? <I.Hash/> : <I.Vol/>} <span className="tv-heading">{activeChannel?.name}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {[
                        { icon: <I.Phone/>,        label: 'Voice Call',   fn: () => activeChannel && joinVoice(activeChannel) },
                        { icon: <I.Cam off={false}/>, label: 'Video Call', fn: () => activeChannel && joinVoice(activeChannel, true) },
                        { icon: <I.Screen/>,        label: 'Screen Share', fn: () => activeChannel && joinVoice(activeChannel, true).then(() => rtc.toggleScreenShare()) },
                      ].map(b => <button key={b.label} title={b.label} onClick={b.fn} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: 7, borderRadius: 8, display: 'flex' }}>{b.icon}</button>)}
                    </div>
                  </div>

                  <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {messages.length === 0 && (
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: C.muted, gap: 8 }}>
                        <div style={{ fontSize: 36 }}>💬</div><div style={{ fontWeight: 600 }}>No messages yet</div>
                      </div>
                    )}
                    {messages.map((msg, i, arr) => {
                      const showHead = i === 0 || arr[i - 1].user_id !== msg.user_id;
                      const timeStr  = msg.created_at ? new Date(msg.created_at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                      return (
                        <div key={msg.id} style={{ display: 'flex', gap: 12, padding: '3px 8px', borderRadius: 8, marginTop: showHead ? 10 : 0, animation: 'fadeUp 0.15s ease' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#111318'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <div style={{ width: 36, flexShrink: 0 }}>
                            {showHead ? <Avatar u={{ username: msg.username, avatar: msg.avatar }} size={36}/> : <div style={{ width: 36, textAlign: 'center', fontSize: 10, color: '#1E2128', paddingTop: 5 }}>{timeStr.split(' ')[0]}</div>}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {showHead && (
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                                <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{msg.username}</span>
                                <span style={{ fontSize: 11, color: C.muted }}>{timeStr}</span>
                                {msg.edited_at && <span style={{ fontSize: 10, color: C.muted }}>(edited)</span>}
                              </div>
                            )}
                            <div style={{ fontSize: 14, color: C.text, lineHeight: 1.5, wordBreak: 'break-word' }}>{msg.content}</div>
                            {msg.reactions?.length > 0 && (
                              <div style={{ display: 'flex', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
                                {msg.reactions.map(r => (
                                  <button key={r.emoji} onClick={() => toggleReaction(msg.id, r.emoji)} style={{ background: '#1A1D24', border: `1px solid ${C.border}`, borderRadius: 20, padding: '2px 8px', cursor: 'pointer', fontSize: 13, color: C.text, display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {r.emoji} <span style={{ fontSize: 12, fontWeight: 600 }}>{r.count}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {typing.length > 0 && <div style={{ padding: '4px 56px', fontSize: 12, color: C.muted, fontStyle: 'italic' }}>{typing.map(t => t.username).join(', ')} {typing.length === 1 ? 'is' : 'are'} typing...</div>}
                    <div ref={bottomRef}/>
                  </div>

                  <div style={{ padding: '0 16px 16px', flexShrink: 0 }}>
                    <div style={{ background: '#111318', border: `1px solid ${C.border}`, borderRadius: 12, display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px' }}>
                      <input value={input} onChange={e => handleInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                        placeholder={`Message #${activeChannel?.name || '...'}`}
                        style={{ flex: 1, background: 'none', border: 'none', color: C.text, fontSize: 14, outline: 'none', padding: '13px 4px' }}/>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex', padding: 4 }}><I.Smile/></button>
                      <button onClick={sendMessage} style={{ background: input.trim() ? C.accent : '#1A1D24', border: 'none', borderRadius: 8, cursor: 'pointer', color: input.trim() ? '#0A0C10' : C.muted, padding: '6px 10px', display: 'flex', transition: 'all 0.15s' }}><I.Send/></button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── 4. Members panel ── */}
      {!isMobile && (
        <div style={{ width: 210, background: '#0E1015', borderLeft: `1px solid ${C.border}`, overflow: 'auto', overflowX: 'hidden', padding: '14px 10px', minHeight: 0, alignSelf: 'stretch' }}>
          {[{ label: 'Online', list: onlineMembers }, { label: 'Offline', list: offlineMembers }].map(({ label, list }) => list.length > 0 && (
            <div key={label} style={{ marginBottom: 16 }}>
              <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, padding: '0 8px 6px' }}>{label} — {list.length}</div>
              {list.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8, cursor: 'pointer', opacity: label === 'Offline' ? 0.5 : 1, position: 'relative' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#1A1D24'; e.currentTarget.querySelector('.call-btns')?.style && (e.currentTarget.querySelector('.call-btns').style.display = 'flex'); }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('.call-btns')?.style && (e.currentTarget.querySelector('.call-btns').style.display = 'none'); }}>
                  <div style={{ position: 'relative' }}>
                    <Avatar u={m} size={28}/>
                    <div style={{ position: 'absolute', bottom: -1, right: -1, width: 9, height: 9, borderRadius: '50%', background: label === 'Online' ? '#22C55E' : '#374151', border: `2px solid ${C.sidebar}` }}/>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.username}</div>
                    {m.role !== 'member' && <div style={{ fontSize: 10, color: m.role === 'owner' || m.role === 'admin' ? C.accent : '#F59E0B' }}>{m.role}</div>}
                  </div>
                  {label === 'Online' && m.id !== user?.id && (
                    <div className="call-btns" style={{ display: 'none', gap: 4, flexShrink: 0 }}>
                      <button title={`Voice call ${m.username}`} onClick={e => { e.stopPropagation(); startPrivateCall(m, false); }}
                        style={{ background: '#22C55E22', border: '1px solid #22C55E44', borderRadius: 6, color: '#22C55E', width: 26, height: 26, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>📞</button>
                      <button title={`Video call ${m.username}`} onClick={e => { e.stopPropagation(); startPrivateCall(m, true); }}
                        style={{ background: `${C.accent}22`, border: `1px solid ${C.accent}44`, borderRadius: 6, color: C.accent, width: 26, height: 26, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>📹</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      </div>

      {/* Server context menu */}
      {serverMenu && (
        <div onClick={() => setServerMenu(null)} style={{ position: 'fixed', inset: 0, zIndex: 500 }}>
          <div onClick={e => e.stopPropagation()} style={{
            position: 'fixed',
            // Clamp to viewport: if too close to right edge, anchor to right side of click point
            left: Math.min(serverMenu.x, window.innerWidth - 196),
            top: Math.min(serverMenu.y, window.innerHeight - 200),
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 6, minWidth: 180, boxShadow: '0 8px 32px rgba(0,0,0,0.6)', zIndex: 501
          }}>
            <div style={{ padding: '6px 12px 10px', borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{serverMenu.server?.name}</div>
            </div>
            {[
              { label: '✏️  Rename Server',   fn: () => renameServer(serverMenu.server) },
              { label: '👥  Manage Members',  fn: () => { openMemberPanel(); setServerMenu(null); } },
              isAdmin && { label: '🛡  Admin Panel', fn: openAdminPanel },
              { label: '🔴  Go Live (Stream)', fn: () => { window.open('/live?stream=1', '_blank'); setServerMenu(null); } },
              { label: '🗑️  Delete Server',   fn: () => deleteServer(serverMenu.server), danger: true },
            ].filter(Boolean).map(item => (
              <button key={item.label} onClick={item.fn} style={{ width: '100%', display: 'block', textAlign: 'left', padding: '8px 12px', border: 'none', background: 'none', color: item.danger ? '#EF4444' : C.text, cursor: 'pointer', borderRadius: 8, fontSize: 13 }}
                onMouseEnter={e => e.currentTarget.style.background = '#1A1D24'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>{item.label}</button>
            ))}
          </div>
        </div>
      )}

      {/* Member management panel */}
      {showMemberPanel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: 28, width: 'min(460px, calc(100vw - 32px))', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ flex: 1, fontSize: 18, fontWeight: 700, color: C.text }}>👥 Members — {activeServer?.name}</div>
              <button onClick={() => setShowMemberPanel(false)} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 20 }}>✕</button>
            </div>
            <div style={{ overflow: 'auto', flex: 1 }}>
              {serverMembers.filter(m => m.username !== 'system').map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
                  <Avatar u={m} size={34}/>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{m.username}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{m.email || m.provider} · <span style={{ color: m.role === 'owner' ? C.accent : C.muted }}>{m.role}</span></div>
                  </div>
                  {m.id !== user?.id && (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button title={`Voice call ${m.username}`} onClick={() => { startPrivateCall(m, false); setShowMemberPanel(false); }}
                        style={{ background: '#22C55E22', border: '1px solid #22C55E44', borderRadius: 8, color: '#22C55E', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>📞</button>
                      <button title={`Video call ${m.username}`} onClick={() => { startPrivateCall(m, true); setShowMemberPanel(false); }}
                        style={{ background: `${C.accent}22`, border: `1px solid ${C.accent}44`, borderRadius: 8, color: C.accent, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>📹</button>
                      {m.role !== 'owner' && isAdmin && (
                        <>
                          <button onClick={() => kickMember(m.id)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'none', color: C.muted, cursor: 'pointer' }}>Kick</button>
                          <button onClick={() => banMember(m.id, m.username)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 8, border: '1px solid #EF444433', background: 'none', color: '#EF4444', cursor: 'pointer' }}>Ban</button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showAdminPanel && <AdminPanel socket={socket} onClose={() => setShowAdminPanel(false)}/>}

      {/* Incoming call toast */}
      {incomingCall && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 600, background: C.card, border: `1px solid ${C.accent}`, borderRadius: 16, padding: '16px 20px', boxShadow: '0 8px 32px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', gap: 12, minWidth: 280, backdropFilter: 'blur(8px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 28, animation: 'candleFlicker 1s ease-in-out infinite' }}>{incomingCall.withVideo ? '📹' : '📞'}</div>
            <div>
              <div style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>{incomingCall.fromUsername}</div>
              <div style={{ color: C.muted, fontSize: 12 }}>Incoming {incomingCall.withVideo ? 'video' : 'voice'} call</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={async () => {
              const { roomId, withVideo, fromUsername } = incomingCall;
              setIncomingCall(null);
              if (inCall) { rtc.leaveChannel(); setInCall(null); }
              await rtc.joinChannel(roomId, { video: withVideo });
              setInCall({ channelId: roomId, name: `Call with ${fromUsername}`, isPrivate: true });
            }} style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', cursor: 'pointer', background: '#22C55E', color: '#fff', fontWeight: 700, fontSize: 13 }}>Accept</button>
            <button onClick={() => setIncomingCall(null)} style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', cursor: 'pointer', background: '#EF4444', color: '#fff', fontWeight: 700, fontSize: 13 }}>Decline</button>
          </div>
        </div>
      )}
    </div>
    </ThemeCtx.Provider>
  );
}
